import { Component } from '@theme/component';

/**
 * <q-nav-indicator> — the sliding pill behind the header's category links
 * (Vercel / Linear / shadcn "pill nav"): it rests on the current page's link,
 * glides to whichever link is hovered or focused, and glides back when the
 * pointer or focus leaves the menu. One element moves, so there is one
 * highlight at a time and no flicker between links.
 *
 * Lives inside Horizon's <nav class="menu-list"> (blocks/_header-menu.liquid),
 * next to its <overflow-list>; positions are measured relative to that nav,
 * so RTL and Horizon's "More" overflow need no special cases: links that
 * moved into "More" (slot="overflow") are simply not targets.
 *
 * Fail-safe: until this runs (or with JS off) the current link carries a
 * static pill in CSS; `data-ready` on this element hands over to it. All
 * state lives on this element, which carries `data-skip-node-update`:
 * Horizon re-hydrates the header from server HTML after load, and morph
 * would otherwise strip it. Reduced motion: it jumps instead of gliding (CSS).
 *
 * States (data-state): `active` resting on the current page · `hover` on a
 * hovered/focused link · absent = hidden (no current link, nothing hovered).
 * Styles: assets/custom-header.css §3.
 */
class NavIndicator extends Component {
  /** @type {HTMLElement | null} */
  #nav = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  /** @type {MutationObserver | null} */
  #mutationObserver = null;
  /** Pointer or focus is inside the menu: don't snap back while it is */
  #engaged = false;

  connectedCallback() {
    super.connectedCallback();
    this.#nav = this.closest('.menu-list');
    if (!this.#nav) return;

    this.dataset.ready = '';
    this.#nav.addEventListener('pointerover', this.#onPointerOver);
    this.#nav.addEventListener('pointerleave', this.#rest);
    this.#nav.addEventListener('focusin', this.#onFocusIn);
    this.#nav.addEventListener('focusout', this.#onFocusOut);

    // Horizon's <overflow-list> lays the row out after this runs and moves
    // links in and out of "More" (slot / aria-hidden) as the row narrows;
    // fonts and resizes change widths. Re-settle on all of them.
    const settle = () => {
      if (!this.#engaged) this.#rest({ instant: true });
    };
    this.#resizeObserver = new ResizeObserver(settle);
    this.#resizeObserver.observe(this.#nav);
    // Attribute changes arrive in bursts (hover classes, overflow slots);
    // measure once, on the next frame, rather than forcing a layout for each
    let frame = 0;
    this.#mutationObserver = new MutationObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(settle);
    });
    this.#mutationObserver.observe(this.#nav, {
      subtree: true,
      attributes: true,
      attributeFilter: ['slot', 'aria-hidden', 'class', 'aria-current'],
    });
    document.fonts?.ready.then(settle);
    window.addEventListener('load', settle, { once: true });
    requestAnimationFrame(settle);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (!this.#nav) return;
    delete this.dataset.ready;
    this.#nav.removeEventListener('pointerover', this.#onPointerOver);
    this.#nav.removeEventListener('pointerleave', this.#rest);
    this.#nav.removeEventListener('focusin', this.#onFocusIn);
    this.#nav.removeEventListener('focusout', this.#onFocusOut);
    this.#resizeObserver?.disconnect();
    this.#mutationObserver?.disconnect();
  }

  /**
   * The pill target for a link: its title span (the visible label + padding).
   * @param {Element | null} element - Any element inside a menu item
   * @returns {HTMLElement | null}
   */
  #targetFor(element) {
    const item = element?.closest('.menu-list__list-item');
    if (!item || item.getAttribute('slot') === 'overflow') return null;
    /** @type {HTMLElement | null} */
    const title = item.querySelector(':scope > a .menu-list__link-title, :scope > .menu-list__trigger > a .menu-list__link-title, :scope > button .menu-list__link-title');
    return title && title.getClientRects().length ? title : null;
  }

  /** @returns {HTMLElement | null} The current page's link title, if it is in the row */
  #activeTarget() {
    const link = this.#nav?.querySelector(
      '.menu-list__list-item:not([slot="overflow"]) :is(.menu-list__link--active, [aria-current="page"])'
    );
    return this.#targetFor(link ?? null);
  }

  /**
   * @param {HTMLElement | null} target
   * @param {'active' | 'hover'} state
   * @param {boolean} [instant] - Skip the glide (first paint, resize)
   */
  #moveTo(target, state, instant = false) {
    if (!target || !this.#nav) {
      delete this.dataset.state;
      return;
    }
    const navBox = this.#nav.getBoundingClientRect();
    const box = target.getBoundingClientRect();

    if (instant) this.dataset.instant = '';
    this.style.setProperty('--q-pill-x', `${box.left - navBox.left}px`);
    this.style.setProperty('--q-pill-y', `${box.top - navBox.top}px`);
    this.style.setProperty('--q-pill-w', `${box.width}px`);
    this.style.setProperty('--q-pill-h', `${box.height}px`);
    this.dataset.state = state;
    if (instant) requestAnimationFrame(() => delete this.dataset.instant);
  }

  /** @param {{ instant?: boolean } | Event} [options] */
  #rest = (options) => {
    if (options instanceof Event) this.#engaged = false;
    const instant = !(options instanceof Event) && Boolean(options?.instant);
    this.#moveTo(this.#activeTarget(), 'active', instant);
  };

  /** @param {PointerEvent} event */
  #onPointerOver = (event) => {
    if (event.pointerType === 'touch') return;
    this.#engaged = true;
    this.#show(/** @type {Element} */ (event.target));
  };

  /** @param {FocusEvent} event */
  #onFocusIn = (event) => {
    this.#engaged = true;
    this.#show(/** @type {Element} */ (event.target));
  };

  /**
   * Pill on the link that holds `element`: gold when it is the current page.
   * @param {Element} element
   */
  #show(element) {
    const target = this.#targetFor(element);
    if (!target) return;
    const link = target.closest('a, button');
    const isCurrent = !!link && (link.classList.contains('menu-list__link--active') || link.getAttribute('aria-current') === 'page');
    this.#moveTo(target, isCurrent ? 'active' : 'hover');
  }

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    if (this.#nav?.contains(/** @type {Node | null} */ (event.relatedTarget))) return;
    this.#engaged = false;
    this.#rest();
  };
}

if (!customElements.get('q-nav-indicator')) {
  customElements.define('q-nav-indicator', NavIndicator);
}
